import { useIsAuthenticated } from "@azure/msal-react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import TransactionService, {
  AttributeChangeHeaderStatus,
  B2CTransaction,
} from "../../services/transactionService";
import Grid from "@mui/material/Unstable_Grid2";
import {
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Collapse,
  IconButton,
  IconButtonProps,
  styled,
  Tooltip,
  Typography,
  Paper,
  Divider,
  Chip,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import { Box } from "@mui/system";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Progress from "../progress/Progress";
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import { getAttributeContent } from "./AttributeNameMap";
import { useLocales } from "../../locales";
import { useSettingsContext } from "../settings/SettingsContext";

const tokenParamName = "attToken";

interface ExpandMoreProps extends IconButtonProps {
  expand: boolean;
}

const ExpandMore = styled((props: ExpandMoreProps) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? "rotate(0deg)" : "rotate(180deg)",
  marginLeft: "auto",
  transition: theme.transitions.create("transform", {
    duration: theme.transitions.duration.shortest,
  }),
}));

export interface AttributeCheckProps {
  isExpanded: boolean;
  transactionProp: B2CTransaction | null;
}

export default function AttributeCheck({
  isExpanded = false,
  transactionProp,
}: AttributeCheckProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { translate, currentLang } = useLocales();
  const { themeDirection } = useSettingsContext();
  
  // RTL languages detection
  const rtlLanguages = ['ar', 'ar-IL', 'he', 'he-IL'];
  const isRTL = rtlLanguages.includes(currentLang.value) || themeDirection === 'rtl';
  
  // Debug: Log RTL status
  console.log('Current language:', currentLang.value, 'Theme direction:', themeDirection, 'Is RTL:', isRTL);
  
  const isAuthenticated = useIsAuthenticated();
  const [transaction, setTransaction] = useState<B2CTransaction | null>(transactionProp);
  const [expanded, setExpanded] = useState(isExpanded);
  const [progressVisible, setProgressVisible] = useState(false);
  const expandVisible = transaction?.attributeChangeDetails.length || 0 > 0;
  const tokenParam = searchParams.get(tokenParamName);

  const handleExpandClick = () => {
    setExpanded(!expanded);
    if (transaction?.attributeChangeHeader.transactionId) {
      setViewed(transaction.attributeChangeHeader.transactionId);
    }
  };

  useEffect(() => {
    const token = searchParams.get(tokenParamName);
    if (token && isAuthenticated) {
      (async () => {
        setProgressVisible(true);
        try {
          const data = await TransactionService.getTransaction(token);
          setTransaction(data.data);
          setExpanded(true);
        } finally {
          setProgressVisible(false);
        }
      })();
    }
  }, [searchParams, isAuthenticated]);

  const setTransactionStatus = async (transactionId: string, accepted: boolean) => {
    setProgressVisible(true);
    try {
      await TransactionService.updateTransactionStatus(transactionId, accepted);
      const data = await TransactionService.getTransaction(transactionId);
      setTransaction(data.data);
    } finally {
      setProgressVisible(false);
    }
  };

  const setViewed = async (transactionId: string) => {
    setProgressVisible(true);
    try {
      if (transaction?.attributeChangeHeader.status === 100) {
        await TransactionService.setViewed(transactionId);
        const data = await TransactionService.getTransaction(transactionId);
        setTransaction(data.data);
      }
    } finally {
      setProgressVisible(false);
    }
  };

  return (
    <>
      <Progress visible={progressVisible}/>
      {transaction && (
        <Card sx={{ 
          m: { xs: 1, sm: 1.5 },
          boxShadow: 2,
          borderRadius: 2,
          overflow: 'hidden'
        }}>
          {/* Header with gradient background */}
          <Box sx={{
            background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)',
            p: { xs: 1.5, sm: 2 },
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
              <Typography variant="h6" sx={{ 
                fontWeight: 600,
                color: 'text.primary',
                textAlign: isRTL ? 'right' : 'left'
              }}>
                {String(translate("attributePage.title"))}
              </Typography>
              <Chip 
                label={String(translate(AttributeChangeHeaderStatus[transaction.attributeChangeHeader.status]))}
                size="small"
                color={transaction.attributeChangeHeader.status <= 200 ? "success" : "default"}
                variant="filled"
              />
            </Stack>
          </Box>

          {/* Main Content */}
          <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
            {/* Transaction Details in Compact Grid */}
            <Grid container spacing={{ xs: 1, sm: 1.5 }} sx={{ mb: 2 }}>
              <Grid xs={12} sm={4}>
                <Box sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ 
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5
                  }}>
                    {String(translate("attributePage.dateTime"))}
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    fontWeight: 500,
                    mt: 0.25,
                    color: 'text.primary'
                  }}>
                    {new Date(transaction.attributeChangeHeader.notificationSentTime).toLocaleString()}
                  </Typography>
                </Box>
              </Grid>
              
              <Grid xs={12} sm={4}>
                <Box sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ 
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5
                  }}>
                    {String(translate("attributePage.notificationType"))}
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    fontWeight: 500,
                    mt: 0.25,
                    color: 'text.primary'
                  }}>
                    {String(translate(transaction.attributeChangeHeader.notificationType.toLowerCase()))}
                  </Typography>
                </Box>
              </Grid>
              
              <Grid xs={12} sm={4}>
                <Box sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ 
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5
                  }}>
                    {String(translate("attributePage.notificationValue"))}
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    fontWeight: 500,
                    mt: 0.25,
                    color: 'text.primary'
                  }}>
                    {transaction.attributeChangeHeader.notificationValue}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Action Buttons - Break out of CardContent padding */}
            <Box 
              sx={{ 
                mt: 1.5,
                mx: { xs: -1.5, sm: -2 }, // Negative margin to counteract CardContent padding
                px: { xs: 1.5, sm: 2 }, // Add padding back for button spacing
                width: 'auto', // Let it extend to card edges
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 1,
                alignItems: { xs: 'stretch', sm: 'center' },
                // Use float for RTL positioning
                ...(isRTL ? {
                  '& > button': {
                    float: 'right !important'
                  }
                } : {
                  '& > button': {
                    float: 'left !important'
                  }
                }),
                // Clear floats
                '&::after': {
                  content: '""',
                  display: 'table',
                  clear: 'both'
                }
              }}
            >
              <Button 
                variant="contained" 
                color="success"
                size="small"
                sx={{ 
                  minWidth: { xs: 'auto', sm: 100 },
                  // Override float on mobile
                  float: { xs: 'none !important', sm: 'unset' }
                }}
                onClick={async () => {
                  await setTransactionStatus(transaction.attributeChangeHeader.transactionId, true);
                }}
                disabled={progressVisible || transaction.attributeChangeHeader.status > 200}
              >
                {String(translate("attributePage.accept"))}
              </Button>
              
              <Button 
                variant="contained" 
                color="error"
                size="small"
                sx={{ 
                  minWidth: { xs: 'auto', sm: 100 },
                  // Override float on mobile
                  float: { xs: 'none !important', sm: 'unset' }
                }}
                onClick={async () => {
                  await setTransactionStatus(transaction.attributeChangeHeader.transactionId, false);
                }}
                disabled={progressVisible || transaction.attributeChangeHeader.status > 200}
              >
                {String(translate("attributePage.reject"))}
              </Button>
            </Box>
          </CardContent>

          {/* Expandable Section for Changes */}
          {expandVisible && (
            <CardActions sx={{ 
              p: { xs: 1, sm: 1.5 },
              backgroundColor: 'grey.50',
              borderTop: '1px solid',
              borderColor: 'divider',
              // Apply same RTL alignment logic as Accept/Reject buttons
              display: 'flex',
              alignItems: 'center',
              // Use float for RTL positioning
              ...(isRTL ? {
                '& > button': {
                  float: 'right !important'
                }
              } : {
                '& > button': {
                  float: 'left !important'
                }
              }),
              // Clear floats
              '&::after': {
                content: '""',
                display: 'table',
                clear: 'both'
              }
            }}>
              <Button
                onClick={handleExpandClick}
                startIcon={expanded ? <KeyboardDoubleArrowUpIcon /> : <KeyboardDoubleArrowDownIcon />}
                variant="outlined"
                size="small"
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  borderRadius: 2,
                  px: 2,
                  minWidth: 100 // Same minimum width as Accept/Reject buttons
                }}
              >
                {String(translate("attributePage.viewChanges"))}
              </Button>
            </CardActions>
          )}

          {/* Collapsible Content */}
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Divider />
            <Box sx={{ p: { xs: 1.5, sm: 2 }, pt: { xs: 1, sm: 1.5 } }}>
              <Stack spacing={{ xs: 1.5, sm: 2 }}>
                {transaction.attributeChangeDetails.map((detail, index) => (
                  <Paper key={detail.id} variant="outlined" sx={{ 
                    p: { xs: 1.5, sm: 2 },
                    backgroundColor: 'grey.25',
                    border: '1px solid',
                    borderColor: 'divider'
                  }}>
                    <Typography variant="subtitle2" sx={{ 
                      fontWeight: 600,
                      mb: 1.5,
                      color: 'text.primary',
                      textAlign: isRTL ? 'right' : 'left'
                    }}>
                      {getAttributeContent(detail.attributeName)}
                    </Typography>
                    
                    <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                      {/* Old Value */}
                      <Grid xs={12} md={6}>
                        <Box sx={{ 
                          p: 1.5,
                          border: '2px solid',
                          borderColor: 'error.light',
                          borderRadius: 2,
                          backgroundColor: 'error.lighter',
                          textAlign: isRTL ? 'right' : 'left'
                        }}>
                          <Typography variant="caption" color="error.main" sx={{ 
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            mb: 0.5,
                            display: 'block'
                          }}>
                            {String(translate("attributePage.oldValue"))}
                          </Typography>
                          <Typography variant="body2" sx={{ 
                            fontWeight: 500,
                            color: 'error.dark',
                            wordBreak: 'break-word',
                            minHeight: 20
                          }}>
                            {detail.oldValue || '-'}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* New Value */}
                      <Grid xs={12} md={6}>
                        <Box sx={{ 
                          p: 1.5,
                          border: '2px solid',
                          borderColor: 'success.light',
                          borderRadius: 2,
                          backgroundColor: 'success.lighter',
                          textAlign: isRTL ? 'right' : 'left'
                        }}>
                          <Typography variant="caption" color="success.main" sx={{ 
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            mb: 0.5,
                            display: 'block'
                          }}>
                            {String(translate("attributePage.newValue"))}
                          </Typography>
                          <Typography variant="body2" sx={{ 
                            fontWeight: 500,
                            color: 'success.dark',
                            wordBreak: 'break-word',
                            minHeight: 20
                          }}>
                            {detail.newValue || '-'}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </Stack>
            </Box>
          </Collapse>
        </Card>
      )}
    </>
  );
}


